import { useState, useEffect } from "react"
import type React from "react";
import logo from "../../assets/logo.svg"
import axios from "axios"
import { Eye, EyeOff, Loader2 } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { getAuthServiceUrl, getLegacyAuthLoginUrl, LEGACY_LOGIN_ROLE, persistAuthSession, persistLegacyAuthSession } from "../../utils/auth"

interface FormData {
  email: string
  password: string
}

interface FormErrors {
  email?: string
  password?: string
}

interface GrantOption {
  grantId: number
  resourceId: number
  role: string
  permissions: string[]
}

interface LoginUser {
  userId: number
  email: string
  name: string
  userPreferences?: Record<string, unknown>
}

const LoginPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>("")
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
  })
  const [grantOptions, setGrantOptions] = useState<GrantOption[]>([])
  const [pendingToken, setPendingToken] = useState<string | null>(null)
  const [loginUser, setLoginUser] = useState<LoginUser | null>(null)
  const [selectingGrant, setSelectingGrant] = useState(false)
  const navigate = useNavigate();

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setFormData({
      ...formData,
      [id]: value,
    })

    setFormErrors({
      ...formErrors,
      [id]: "",
    })
  }

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {}
    if (!formData.email || !validateEmail(formData.email)) {
      errors.email = "Please enter a valid email."
    }
    if (!formData.password) {
      errors.password = "Please enter a valid password."
    }
    return errors
  }

  const finishLogin = (
    token: string,
    session?: {
      resourceId?: number
      lobId?: number
      role?: string
      permissions?: string[]
    },
    user?: LoginUser
  ) => {
    persistAuthSession(token, session)
    if (user) {
      const lobId = session?.lobId ?? session?.resourceId
      localStorage.setItem("userDetails", JSON.stringify({
        ...user,
        ...(lobId != null ? { lobId, resourceId: lobId } : {}),
      }))
    }
    navigate("/", { replace: true })
  }

  const handleSelectGrant = async (grantId: number) => {
    if (!pendingToken) return
    setSelectingGrant(true)
    setError("")
    try {
      const response = await axios.post(
        `${getAuthServiceUrl()}/auth/select-grant`,
        { grantId, portalCode: "admin" },
        {
          headers: {
            Authorization: `Bearer ${pendingToken}`,
          },
        }
      )
      finishLogin(response.data.token, response.data.session, loginUser || undefined)
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to select grant")
    } finally {
      setSelectingGrant(false)
    }
  }

  const attemptPortalLogin = async () => {
    const response = await axios.post(`${getAuthServiceUrl()}/auth/login`, {
      ...formData,
      portalCode: "admin",
    })

    const data = response.data
    setLoginUser(data.user)

    if (data.token) {
      finishLogin(data.token, data.session, data.user)
      return
    }

    if (data.pendingToken && data.grants?.length) {
      setPendingToken(data.pendingToken)
      setGrantOptions(data.grants)
      return
    }

    throw new Error("Login failed. No admin access found.")
  }

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    setLoading(true)
    setFormErrors({})
    setError("")
    setGrantOptions([])
    setPendingToken(null)

    try {
      try {
        const response = await axios.post(getLegacyAuthLoginUrl(), {
          ...formData,
          role: LEGACY_LOGIN_ROLE,
        })
        const { user, tokens } = response.data
        persistLegacyAuthSession(tokens.access.token, tokens.refresh.token, user)
        navigate('/', { replace: true })
        return
      } catch {
        // Legacy login failed — fall back to auth-service login.
      }

      await attemptPortalLogin()
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || "Invalid credentials"
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("")
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [error])

  const showGrantPicker = grantOptions.length > 0 && pendingToken

  return (
    <div className="min-h-screen bg-gradient-to-br  from-gray-50 to-gray-100 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg border-t-4 border-brand/70 p-8">
          <div className="flex flex-col items-center mb-2">
            <img 
              src={logo} 
              alt="Fincentive Logo" 
              className="h-14 w-auto drop-shadow-md mt-4 mb-3" 
              style={{ maxWidth: '220px' }}
            />
          </div>

          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-green-600 animate-spin mb-4" />
              <p className="text-gray-600">Signing you in...</p>
            </div>
          )}

          {!loading && showGrantPicker && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2 text-center">Select admin access</h2>
              <p className="text-sm text-gray-600 mb-4 text-center">Choose which role to use:</p>
              <ul className="space-y-2">
                {grantOptions.map((grant) => (
                  <li key={grant.grantId}>
                    <button
                      type="button"
                      disabled={selectingGrant}
                      onClick={() => handleSelectGrant(grant.grantId)}
                      className="w-full text-left px-4 py-3 rounded-md border border-gray-200 hover:border-green-500 hover:bg-green-50 transition-colors disabled:opacity-50"
                    >
                      <span className="font-medium">{grant.role}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!loading && !showGrantPicker && (
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className={`w-full px-3 py-2 text-sm border rounded-md  focus:outline-none  transition-colors ${
                    formErrors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                )}
              </div>

              <div className="mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className={`w-full px-3 py-2 text-sm pr-10 border rounded-md  focus:outline-none   transition-colors ${
                      formErrors.password ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                )}
              </div>

              <div className="text-right mb-4">
                <a 
                  href="/forgot-password" 
                  className="text-sm text-green-600 hover:text-green-700 font-medium transition-colors"
                >
                  Forgot your password?
                </a>
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 font-medium text-white py-2 px-3 text-md rounded-md hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              >
                Sign In
              </button>
            </form>
          )}
        </div>
      </div>

      {error && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-white rounded-full"></div>
            <span className="text-sm font-medium">{error}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoginPage
